import { PrinterProfile } from '../../types/printerProfile';
import { PortedTicketData as TicketData } from './types';
import {
  buildTicketPlan,
  renderNativeColumns,
  joinTicketLines,
  PlanLine,
} from './ticketTestPlan';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TEST 4 — « SANS ANCRE » : colonnes natives + interdiction du mot `ARTICLES`
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * HYPOTHÈSE TESTÉE (la plus forte, et la plus rentable si elle tient) :
 * le pré-traitement natif qui détruit la colonne des prix n'est PAS inconditionnel — il
 * est armé par une SEULE ligne. `MainActivity:4806` :
 *
 *     if (line.trim().equalsIgnoreCase("ARTICLES")) { inArticlesSection = true; … }
 *
 * et `inArticlesSection` n'a que DEUX points d'affectation dans tout le fichier
 * (`MainActivity:2759` chemin Star Bluetooth, `MainActivity:4806` chemin ESC/POS), tous
 * deux gardés par ce même test. Si aucune ligne ne vaut exactement `ARTICLES` après
 * `trim()`, alors TOUT le bloc de réécriture est inerte :
 *   • la destruction des prix (`.*x\d+.*` → `[L]<nom> x<qté>`),
 *   • la réécriture des lignes `taille:`,
 *   • la réémission des lignes `€\d+\.\d{2}`.
 *
 * CE QUE CE MODULE PROUVE : il écrit délibérément les quantités sous la forme la plus
 * dangereuse qui soit — `Pizza Margherita x2` — dans des COLONNES NATIVES `[L]…[R]`.
 * Si les prix sortent intacts, l'ancre est bien le seul verrou, et le rendu ESC/POS
 * natif complet reste disponible sans image ni contorsion sur les libellés.
 * Si les prix sont détruits, l'hypothèse tombe et seule l'image (TEST 1 / TEST 2) sauve
 * la colonne des montants.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * COMMENT L'ANCRE EST NEUTRALISÉE
 * ───────────────────────────────────────────────────────────────────────────────
 * • l'en-tête de section est rendu `[C]- - ARTICLES - -` : `trim()` ne vaut pas
 *   `ARTICLES`, et le `[C]` n'est même pas retiré avant le test (le natif compare la
 *   ligne BRUTE) — double sécurité ;
 * • toute AUTRE ligne qui vaudrait accidentellement `ARTICLES` (catégorie ainsi nommée,
 *   note client…) reçoit un ` *` en suffixe — voir `renderNativeColumns`.
 *
 * ⚠️ CIBLES : chemin ESC/POS uniquement (chinoises génériques, Epson TM-T20 en
 * USB/Bluetooth/TCP). Sur Star USB les balises sont SUPPRIMÉES sans remplissage et sur
 * Sunmi elles s'impriment EN CLAIR : ce module n'est pas fait pour elles — c'est le
 * module unifié qui couvre ce besoin-là.
 */

const widthOf = (profile?: PrinterProfile): number => {
  const w = Number(profile?.config?.width);
  return Number.isFinite(w) && w >= 24 ? Math.min(64, Math.floor(w)) : 48;
};

/**
 * Rendu texte partagé — réutilisé tel quel par le module HYBRIDE (TEST 2).
 * @param separator  Séparateur exploitant déjà rendu SÛR (via `textSafeSeparator`) et tuilé.
 *                   `undefined` → séparateur historique `=` (comportement inchangé du TEST 4).
 */
export const renderNoAnchorLines = (
  lines: PlanLine[],
  width: number,
  emitMarkers = true,
  separator?: string,
  bannerEmphasis = false
): string[] =>
  renderNativeColumns(lines, width, {
    anchorMode: 'decorated',
    killXPattern: false,
    emitMarkers,
    separator,
    bannerEmphasis,
  });

export const buildNoAnchorTicket = (data: TicketData, profile?: PrinterProfile): string => {
  const width = widthOf(profile);
  const plan = buildTicketPlan(data, {
    width,
    // On ARME délibérément le motif dangereux : c'est l'objet même de l'expérience.
    quantityStyle: 'x-suffix',
    emitCategoryMarkers: profile?.config?.showCategories !== false,
  });

  return joinTicketLines(
    renderNoAnchorLines(
      plan.lines,
      plan.width,
      profile?.config?.showCategories !== false,
      undefined,
      // Bandeaux noirs natifs si le profil le demande (profil « USB Epson » notamment) —
      // donne le look du ticket image sans image, donc sans saturer le tampon USB.
      (profile?.config as any)?.bannerEmphasis === true
    )
  );
};
